import axios from "axios";
import { SERVER_ENDPOINT } from "../config";

const BASE = SERVER_ENDPOINT;
const STORAGE_KEY = "offerer_auth_session";
const EXPIRED_FLAG_KEY = "offerer_auth_session_expired";

const http = axios.create({ baseURL: BASE });

/**
 * Every applicant starts from their identification (gov_id, sent without
 * dashes or spaces). Resolves 200 with { status } plus:
 *   "registered" → { first_name, identification_type_id }: has an account,
 *                  log in with a password.
 *   "unclaimed"  → { first_name, email_hint, identification_type_id }: an
 *                  application from before accounts existed; claim it with a
 *                  code (see sendClaimCode()).
 *   "new"        → nothing yet: create the account (see register(), and
 *                  lookupPerson() for the name of a cédula física).
 * identification_type_id is the type that number was registered with (null if
 * unknown): another one chosen in step 1 isn't let through.
 */
export function identify(govId) {
  return http.post("/auth/identify", { gov_id: govId });
}

export function login(govId, password) {
  return http.post("/auth/login", { gov_id: govId, password });
}

/**
 * The name the civil registry (padrón) has for a cédula física: { first_name,
 * last_name, sur_name }, or 404. Public, so "Cree su cuenta" can fill in the
 * name before there's an account.
 */
export function lookupPerson(govId) {
  return http.get(`/persons/${encodeURIComponent(govId)}`);
}

/**
 * The form has a single password field (with "Mostrar"), so the confirmation
 * the backend still asks for is that same value. An email that already has an
 * account without an identification links this one to it when the password
 * matches (200 with linked: true instead of 201). The name goes with it: for a
 * cédula física in the padrón the backend keeps the padrón's.
 */
export function register({ govId, identificationTypeId, firstName, lastName, surName, email, password }) {
  return http.post("/auth/register", {
    gov_id: govId,
    identification_type_id: identificationTypeId,
    first_name: firstName,
    last_name: lastName,
    sur_name: surName,
    email,
    password,
    password_confirmation: password,
  });
}

// Sends the code to the email of the account tied to that identification; resolves with its email_hint.
export function forgotPassword(govId) {
  return http.post("/auth/forgot-password", { gov_id: govId });
}

export function resetPassword(govId, code, newPassword, newPasswordConfirmation) {
  return http.post("/auth/reset-password", {
    gov_id: govId,
    code,
    new_password: newPassword,
    new_password_confirmation: newPasswordConfirmation,
  });
}

// "unclaimed" applications: a code to the email saved in it proves it's theirs before an account takes it over.
export function sendClaimCode(govId) {
  return http.post("/auth/claim/send-code", { gov_id: govId });
}

export function confirmClaim(govId, code, password) {
  return http.post("/auth/claim/confirm", {
    gov_id: govId,
    code,
    password,
    password_confirmation: password,
  });
}

export function saveSession({ user, access }) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ user, access }));
}

export function getSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function clearSession() {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function getAccessToken() {
  const session = getSession();
  return session ? session.access : null;
}

/**
 * The access token lives 20 minutes server-side with no refresh — once it
 * expires, any authenticated request 401s (see the response interceptor in
 * candidateApi.js). This flag survives the reload that follows, so the
 * login screen can explain why the session ended instead of just silently
 * bouncing the applicant back to it.
 */
export function markSessionExpired() {
  sessionStorage.setItem(EXPIRED_FLAG_KEY, "1");
}

export function consumeSessionExpiredFlag() {
  const expired = sessionStorage.getItem(EXPIRED_FLAG_KEY) === "1";
  sessionStorage.removeItem(EXPIRED_FLAG_KEY);
  return expired;
}
